import { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../lib/firebase';
import { 
  User, 
  isSignInWithEmailLink, 
  signInWithEmailLink, 
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth';

interface AuthContextType {
  session: User | null;
  loading: boolean;
  signIn: (email: string) => Promise<{ error: any }>;
  verifyOtp: (email: string, token: string) => Promise<{ error: any }>; // Kept for interface compatibility, not used in Firebase Magic Link
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Handle Firebase Email Link verification
    const handleUrlVerification = async () => {
      if (isSignInWithEmailLink(auth, window.location.href)) {
        setLoading(true);
        let email = window.localStorage.getItem('emailForSignIn');
        if (!email) {
          email = window.prompt('Please provide your email for confirmation');
        }
        
        if (email) {
          try {
            const result = await signInWithEmailLink(auth, email, window.location.href);
            window.localStorage.removeItem('emailForSignIn');
            window.history.replaceState({}, document.title, window.location.pathname);
            setSession(result.user);
          } catch (error) {
            console.error('Error signing in with email link', error);
          }
        }
        setLoading(false);
      }
    };
    
    handleUrlVerification();

    // Listen for changes
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setSession(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (email: string) => {
    // In production, we need to ensure the link redirects to the actual domain
    const redirectUrl = window.location.hostname === 'localhost' 
      ? window.location.origin 
      : 'https://habl.life';
      
    try {
      if (import.meta.env.VITE_FIREBASE_API_KEY === 'your-api-key' || !import.meta.env.VITE_FIREBASE_API_KEY) {
        throw new Error('Firebase is not configured. Please add your credentials to the .env file.');
      }
      
      // We can't generate the link purely on the client with the standard Web SDK.
      // Firebase REST API allows generating the link using returnOobLink: true
      
      const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
      
      const customPayload = {
        requestType: 'EMAIL_SIGNIN',
        email: email,
        continueUrl: redirectUrl,
        canHandleCodeInApp: true,
        returnOobLink: true // THIS tells Firebase to return the link instead of sending the email!
      };
      
      const customResponse = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:sendOobCode?key=${apiKey}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(customPayload)
      });
      
      const customData = await customResponse.json();
      
      if (!customResponse.ok) {
          throw new Error(customData.error?.message || 'Failed to generate auth code');
      }
      
      const link = customData.oobLink;

      // Send the link to our custom PHP mailer
      const response = await fetch('https://habl.life/send_login_email.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
              email: email,
              link: link
          })
      });

      const result = await response.json();
      if (!result.success) {
          throw new Error(result.error || 'Failed to send custom email');
      }

      window.localStorage.setItem('emailForSignIn', email);
      return { error: null };
    } catch (err: any) {
      console.error('Sign in error:', err);
      return { error: err.message || 'Network connection failed' };
    }
  };

  const verifyOtp = async (_email: string, _token: string) => {
    // Not used with Firebase Email Link (which is a true magic link)
    // We keep it to not break the UI component immediately
    return { error: { message: "OTP not supported with Firebase. Please click the link in your email." } };
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
    
    // Clear Local Storage
    localStorage.clear(); 

    // Reset State
    setSession(null);
    
    // Force Reload to clear memory
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ session, loading, signIn, verifyOtp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

